import {
  ENDPOINTS,
  GetBaseDeviationReportRequestSchema,
  ListBaseDeviationReportsRequestSchema,
  RunBaseDeviationReportRequestSchema,
  type GetBaseDeviationReportResponse,
  type ListBaseDeviationReportsResponse,
  type RunBaseDeviationReportResponse,
} from '@clm/shared-types';

import { apiGet, apiPost } from './client';
import { substitutePath } from './verifications';

/**
 * The base-MSA deviation report — CLM-112 (FR-38, NFR-18, DR-11).
 *
 * `listBaseDeviationReports` reads a contract's past runs, its readiness and
 * the current base's (no audit row); `runBaseDeviationReport` computes and
 * stores a new report against a named nomination — current or superseded
 * (R-112-11); `getBaseDeviationReport` re-opens one as computed, beside the
 * live `now` block (the gateway writes the read audit event). All three are
 * `clause:review_match` — Admin and Legal.
 *
 * Every request is validated `.strict()` before anything is sent, and every
 * response is **parsed**, never asserted: the snapshot schema's refinements
 * (a `missing` line with subject paragraphs, a summary that does not count its
 * lines) make an off-contract body a `ZodError`, never a report that says
 * something the API did not.
 *
 * NFR-06: no `scope` key is ever sent — `scope_kind` on the list is how the
 * page learns it. Rule 13: none of these carries contract text; the report
 * page reads slices from `retrieve-contract include: ['full_text']` itself.
 */

const LIST = ENDPOINTS['list-base-deviation-reports'];
const RUN = ENDPOINTS['run-base-deviation-report'];
const GET = ENDPOINTS['get-base-deviation-report'];

export async function listBaseDeviationReports(
  contractKey: string,
  signal?: AbortSignal,
): Promise<ListBaseDeviationReportsResponse> {
  const request = ListBaseDeviationReportsRequestSchema.parse({ contract_key: contractKey });
  const raw = await apiGet<unknown>(
    substitutePath(LIST.path, { contract_key: request.contract_key }),
    signal === undefined ? {} : { signal },
  );
  return LIST.response.parse(raw);
}

export async function runBaseDeviationReport(
  contractKey: string,
  nominationId: string,
  signal?: AbortSignal,
): Promise<RunBaseDeviationReportResponse> {
  const request = RunBaseDeviationReportRequestSchema.parse({ contract_key: contractKey, nomination_id: nominationId });
  /* The key is the path; the body is the rest of the one validated object. */
  const raw = await apiPost<unknown>(
    substitutePath(RUN.path, { contract_key: request.contract_key }),
    { nomination_id: request.nomination_id },
    signal === undefined ? {} : { signal },
  );
  return RUN.response.parse(raw);
}

export async function getBaseDeviationReport(
  reportId: string,
  signal?: AbortSignal,
): Promise<GetBaseDeviationReportResponse> {
  const request = GetBaseDeviationReportRequestSchema.parse({ report_id: reportId });
  const raw = await apiGet<unknown>(
    substitutePath(GET.path, { report_id: request.report_id }),
    signal === undefined ? {} : { signal },
  );
  return GET.response.parse(raw);
}
